
import { ArrowRight, Brain, Check, Lightbulb } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";

const ApproachSection = () => {
  const [activeTab, setActiveTab] = useState<'rational' | 'emotional'>('rational');
  
  return (
    <section className="section-spacing bg-joerg-50">
      <div className="container">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-center mb-2">Mein integrativer Ansatz: Der Mensch im Zentrum</h2>
          <p className="text-center text-joerg-600 mb-8 italic">Die Verbindung zwischen Finanzwissen und emotionaler Intelligenz</p>
          
          <div className="bg-white rounded-xl shadow-soft p-8 mb-10">
            <p className="text-joerg-600 leading-relaxed mb-6 text-left">
              Was mich von traditionellen Beratern unterscheidet: Ich trenne nicht zwischen 'Business' und 'Privat'. In beiden Bereichen stehen Menschen im Mittelpunkt, mit ihren Werten, Ängsten und Zielen.
            </p>
            
            {/* Enhanced Interactive Tabs with improved clickability indicators */}
            <div className="flex flex-col sm:flex-row justify-center gap-3 my-8" role="tablist">
              <button 
                onClick={() => setActiveTab('rational')}
                className={`px-6 py-3 transition-all duration-300 font-medium cursor-pointer rounded-md flex items-center justify-center ${
                  activeTab === 'rational' 
                    ? 'text-white bg-primary shadow-sm' 
                    : 'text-joerg-700 bg-joerg-50 hover:bg-joerg-100'
                }`}
                
                role="tab"
                aria-selected={activeTab === 'rational'}
                aria-controls="rational-content"
              >
                Rationale Dimension
              </button>
              <button 
                onClick={() => setActiveTab('emotional')}
                className={`px-6 py-3 transition-all duration-300 font-medium cursor-pointer rounded-md flex items-center justify-center ${
                  activeTab === 'emotional' 
                    ? 'text-white bg-primary shadow-sm' 
                    : 'text-joerg-700 bg-joerg-50 hover:bg-joerg-100'
                }`}
                
                role="tab"
                aria-selected={activeTab === 'emotional'}
                aria-controls="emotional-content"
              >
                Emotionale Dimension
              </button>
            </div>
            
            {/* Content changes based on active tab - improved accessibility */}
            <div className="min-h-[280px]">
              {activeTab === 'rational' ? (
                <div id="rational-content" className="animate-fade-in" role="tabpanel" aria-labelledby="rational-tab">
                  <h3 className="text-joerg-800 font-medium mb-4">Finanzwissen als Grundlage</h3>
                  <div className="space-y-4 my-6 pl-0.5">
                    <div className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-joerg-700 text-left">
                        <span className="font-medium">Fundiertes Fachwissen</span> – Langjährige Erfahrung im Finanzsektor
                      </span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-joerg-700 text-left">
                        <span className="font-medium">Transparente Analyse</span> – Klare Bewertung von Finanzoptionen ohne versteckte Verkaufsinteressen
                      </span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-joerg-700 text-left">
                        <span className="font-medium">Strategische Planung</span> – Nachhaltige Lösungen statt kurzfristiger Gewinne
                      </span>
                    </div>
                  </div>
                  
                  <div className="bg-joerg-50/70 p-5 rounded-lg mt-6 border-l-4 border-primary">
                    <p className="text-joerg-700 text-left text-sm italic">
                      "Ein Beispiel: Bei einer Anlageentscheidung betrachten wir nicht nur die Rendite, sondern auch wie die Anlage zu Ihrer persönlichen Risikobereitschaft, Ihren Werten und langfristigen Zielen passt."
                    </p>
                  </div>
                </div>
              ) : (
                <div id="emotional-content" className="animate-fade-in" role="tabpanel" aria-labelledby="emotional-tab">
                  <h3 className="text-joerg-800 font-medium mb-4">Emotionale Intelligenz im Finanzkontext</h3>
                  <div className="space-y-4 my-6 pl-0.5">
                    <div className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-joerg-700 text-left">
                        <span className="font-medium">Unbewusste Muster erkennen</span> – Verstehen Sie Ihre emotionalen Reaktionen bei finanziellen Entscheidungen
                      </span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-joerg-700 text-left">
                        <span className="font-medium">Angst- und Stressreduktion</span> – Entwicklung eines gesunden Verhältnisses zu finanziellen Themen
                      </span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-joerg-700 text-left">
                        <span className="font-medium">Selbstwirksamkeit stärken</span> – Aktive Gestaltung Ihrer finanziellen Zukunft
                      </span>
                    </div>
                  </div>
                  
                  <div className="bg-joerg-50/70 p-5 rounded-lg mt-6 border-l-4 border-primary">
                    <p className="text-joerg-700 text-left text-sm italic">
                      "Ein Beispiel: Wir arbeiten daran, wie frühe Erfahrungen mit Geld Ihre heutigen Entscheidungen beeinflussen und entwickeln neue, förderliche neuronale Verknüpfungen für finanzielle Entscheidungen."
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Mobile Hint - only visible on smaller screens */}
            <div className="block sm:hidden text-center mt-6">
              <p className="text-sm text-joerg-500 italic">Tippen Sie auf die Dimensionen oben für mehr Informationen</p>
            </div>
            
            {/* Visual Brain-Finance Connection - Spaced Repetition */}
            <div className="mt-10 p-6 bg-gradient-to-br from-joerg-50/80 to-white/50 rounded-xl border border-joerg-100">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center animate-pulse">
                    <Brain className="h-12 w-12 text-primary" />
                  </div>
                </div>
                <div>
                  <h4 className="text-lg font-medium text-joerg-800 mb-2">Neuroplastizität & Finanzen</h4>
                  <p className="text-joerg-600 text-left">
                    Die neurowissenschaftliche Forschung zeigt: Unser Gehirn kann neue Verknüpfungen bilden und bestehende stärken. Ich nutze diese Erkenntnisse, um mit Ihnen positive finanzielle Denk- und Verhaltensmuster zu entwickeln, die zu nachhaltigen Ergebnissen führen.
                  </p>
                  <Link to="/blog/neuroplastizitaet-epigenetik" className="inline-flex items-center text-primary mt-3 group">
                    <span className="group-hover:underline">Mehr über Neuroplastizität erfahren</span>
                    <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </section>
  );
};

export default ApproachSection;
